import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './custom-texteditor.scss';
import { useTranslation } from 'react-i18next';
import NewButton from '../button/newButton';
import { BoldIcon, ItalicIcon } from './wrappedIcons';

function CommentEditor({
  handleCommentSubmit,
  selectedVendorType,
  toggleCommentDialog,
  commentPayload,
  getEditorValue,
  charLimit,
  placeholder,
}) {
  const [t] = useTranslation('manageorders');
  const editorRef = useRef(null);
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [isFocused, setIsFocused] = useState(false);

  // --- helper: reset formatting state + browser typing state ---
  const resetFormatting = () => {
    // turn off bold/italic typing mode in the browser
    if (document.queryCommandState('bold')) {
      document.execCommand('bold', false, null);
    }
    if (document.queryCommandState('italic')) {
      document.execCommand('italic', false, null);
    }
    document.execCommand('removeFormat', false, null);

    setIsBold(false);
    setIsItalic(false);
  };

  // --- Toggle formatting on button click ---
  const applyStyle = (style) => {
    document.execCommand(style, false, null);
    editorRef.current.focus();

    // Update active state instantly
    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));

    // --- Move cursor outside of tag when disabling ---
    const moveCursorOutsideTag = (tagName) => {
      const sel = window.getSelection();
      if (!sel.rangeCount) return;
      const range = sel.getRangeAt(0);
      let container = range.startContainer;

      while (container && container.nodeType === 3)
        container = container.parentNode;

      if (container && container.tagName === tagName) {
        const parent = container.parentNode;
        const afterNode = document.createTextNode('');
        parent.insertBefore(afterNode, container.nextSibling);

        const newRange = document.createRange();
        newRange.setStart(afterNode, 0);
        newRange.collapse(true);

        sel.removeAllRanges();
        sel.addRange(newRange);
      }
    };

    // Move cursor outside tag if disabled
    if (style === 'bold' && !document.queryCommandState('bold'))
      moveCursorOutsideTag('B');
    if (style === 'italic' && !document.queryCommandState('italic'))
      moveCursorOutsideTag('I');

    getEditorValue(editorRef.current.innerHTML);
  };

  // --- Update active formatting instantly on cursor move ---
  useEffect(() => {
    const updateFormatState = () => {
      if (isFocused) {
        setIsBold(document.queryCommandState('bold'));
        setIsItalic(document.queryCommandState('italic'));
      } else {
        setIsBold(false);
        setIsItalic(false);
      }
    };
    document.addEventListener('selectionchange', updateFormatState);
    return () =>
      document.removeEventListener('selectionchange', updateFormatState);
  }, [isFocused]);

  // --- Focus / blur tracking ---
  useEffect(() => {
    const editor = editorRef.current;
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => {
      setIsFocused(false);
      setIsBold(false);
      setIsItalic(false);
    };

    editor.addEventListener('focus', handleFocus);
    editor.addEventListener('blur', handleBlur);
    return () => {
      editor.removeEventListener('focus', handleFocus);
      editor.removeEventListener('blur', handleBlur);
    };
  }, []);

  // --- Handle key DOWN: char limit / shortcuts ---
  const handleKeyDown = (event) => {
    const editor = editorRef.current;
    const textLength = editor.innerText.replace(/[\n\s]/g, '').length;

    // allow ctrl/cmd + A
    if ((event?.ctrlKey || event?.metaKey) && event?.key === 'a') return;

    // char limit
    if (textLength >= charLimit && event?.key !== 'Backspace') {
      event.preventDefault();
    }
  };

  // --- Handle key UP: update value + reset formatting if empty ---
  const handleKeyUp = () => {
    const editor = editorRef.current;
    const cleanedLength = editor.innerText.replace(/[\n\s]/g, '').length;

    setCharCount(cleanedLength);

    if (cleanedLength === 0) {
      // editor is logically empty → reset everything
      editor.innerHTML = '';
      resetFormatting();
      getEditorValue('');
      return;
    }

    getEditorValue(editor.innerHTML);
  };

  // --- Handle paste with char limit ---
  useEffect(() => {
    const handlePaste = (event) => {
      event.preventDefault();
      const text = event.clipboardData.getData('text');
      const editor = editorRef.current;
      const currentLength = editor.innerText.replace(/[\n\s]/g, '').length;
      const remaining = charLimit - currentLength;
      const textToInsert = text.slice(0, Math.max(remaining, 0));

      document.execCommand('insertText', false, textToInsert);

      const newLength = editor.innerText.replace(/[\n\s]/g, '').length;
      setCharCount(newLength);
      getEditorValue(newLength > 0 ? editor.innerHTML : '');
    };

    const editor = editorRef.current;
    editor.addEventListener('paste', handlePaste);
    return () => editor.removeEventListener('paste', handlePaste);
  }, [charLimit, getEditorValue]);

  // --- Submit comment ---
  const handleSubmit = () => {
    const commentValue = editorRef.current.innerHTML;
    const plainLength = editorRef.current.innerText.replace(
      /[\n\s]/g,
      ''
    ).length;

    handleCommentSubmit(plainLength > 0 ? commentValue : '');
  };

  // --- Load initial content ---
  useEffect(() => {
    const editor = editorRef.current;

    if (selectedVendorType === 'primary')
      editor.innerHTML = commentPayload.primarycomment;
    else if (selectedVendorType === 'pickUp')
      editor.innerHTML = commentPayload.pickupComment;
    else if (selectedVendorType === 'delivery')
      editor.innerHTML = commentPayload.deliverycomment;

    setCharCount(editor.innerText.replace(/[\n\s]/g, '').length);
  }, [commentPayload, selectedVendorType]);

  return (
    <div className="custom-editor no-border">
      <div
        ref={editorRef}
        className={`editor-content boreder-bottom ${
          charLimit ? 'char-limit' : ''
        }`}
        contentEditable
        role="textbox"
        tabIndex="0"
        placeholder={placeholder}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
      />

      <div className="toolbar-wrapper justify-between">
        <div className="toolbar">
          <div className="inline-options">
            <div className="option-wrapper">
              <button
                type="button"
                className={`toolbar-btn${isBold ? ' active' : ''}`}
                onClick={() => applyStyle('bold')}
              >
                <BoldIcon />
              </button>
            </div>
            <div className="option-wrapper">
              <button
                type="button"
                className={`toolbar-btn${isItalic ? ' active' : ''}`}
                onClick={() => applyStyle('italic')}
              >
                <ItalicIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="Comment-btn-area">
          <div className="option-wrapper">
            {charLimit && (
              <div
                className={`char-count ${
                  charCount >= charLimit ? 'exceed-limit' : ''
                }`}
              >
                {charCount}/{charLimit}
              </div>
            )}
            <NewButton
              styles="secondary"
              className="comment-btn"
              onClick={() => {
                handleSubmit();
                toggleCommentDialog();
              }}
            >
              {t('manageordersscreen.bookingdetailsbtn.comment')}
            </NewButton>
          </div>
        </div>
      </div>
    </div>
  );
}

CommentEditor.propTypes = {
  getEditorValue: PropTypes.func,
  commentPayload: PropTypes.objectOf(PropTypes.string),
  toggleCommentDialog: PropTypes.func,
  handleCommentSubmit: PropTypes.func,
  selectedVendorType: PropTypes.string,
  charLimit: PropTypes.number,
  placeholder: PropTypes.string,
};

CommentEditor.defaultProps = {
  getEditorValue: () => {},
  commentPayload: '',
  toggleCommentDialog: () => {},
  handleCommentSubmit: () => {},
  selectedVendorType: '',
  charLimit: 250,
  placeholder: '',
};

export default CommentEditor;
